import { useState, useEffect, useMemo } from 'react';
import { X, Banknote, CreditCard, Tag, Trash2 } from 'lucide-react';
import {
  montantFinal as calcMontantFinal,
  resteAPayer,
  totalEspecesLignes,
  calcRenduEspeces,
  validerLignesPaiement,
  labelMethode,
  roundMoney,
} from '../../utils/caissePaiement';

const METHODS = [
  { id: 'ESPECES', label: 'Espèces', icon: Banknote },
  { id: 'CARTE', label: 'Carte', icon: CreditCard },
  { id: 'CHEQUE', label: 'Chèque', icon: CreditCard },
  { id: 'VIREMENT', label: 'Virement', icon: CreditCard },
];

let ligneId = 0;
const newLigneId = () => `pay-${++ligneId}-${Date.now()}`;

export default function CaissePaymentModal({
  open,
  title,
  subtitle,
  montant,
  reduction = 0,
  fmt,
  codePromo = '',
  setCodePromo,
  onVerifierPromo,
  promoMessage = '',
  promoLoading = false,
  showCodePromo = false,
  error = '',
  onClose,
  onConfirm,
  processing,
}) {
  const [lignes, setLignes] = useState([]);
  const [methode, setMethode] = useState('ESPECES');
  const [montantSaisi, setMontantSaisi] = useState('');
  const [montantRecuEspeces, setMontantRecuEspeces] = useState('');
  const [localHint, setLocalHint] = useState('');

  const du = calcMontantFinal(montant, reduction);
  const reste = du != null ? resteAPayer(du, lignes) : 0;
  const especesTotal = totalEspecesLignes(lignes);
  const rendu = calcRenduEspeces(montantRecuEspeces, lignes);
  const soldeOk = reste <= 0.001 && lignes.length > 0;

  const check = useMemo(
    () =>
      du != null
        ? validerLignesPaiement(du, lignes, montantRecuEspeces)
        : { ok: false, message: '' },
    [du, lignes, montantRecuEspeces]
  );

  useEffect(() => {
    if (!open) return;
    setLignes([]);
    setMethode('ESPECES');
    setMontantSaisi('');
    setMontantRecuEspeces('');
    setLocalHint('');
  }, [open]);

  useEffect(() => {
    if (open && du != null && reste > 0.001) {
      setMontantSaisi(String(reste));
    }
  }, [open, du, reste, lignes.length]);

  useEffect(() => {
    if (soldeOk && especesTotal > 0 && !montantRecuEspeces) {
      setMontantRecuEspeces(String(especesTotal));
    }
  }, [soldeOk, especesTotal, montantRecuEspeces]);

  if (!open) return null;

  const supprimerLigne = (id) => {
    setLignes((prev) => prev.filter((l) => l.id !== id));
    setLocalHint('');
  };

  const enregistrerPaiementCourant = () => {
    const m = roundMoney(montantSaisi);
    if (!m || m <= 0) {
      return { ok: false, message: 'Indiquez un montant supérieur à 0' };
    }
    if (m > reste + 0.001) {
      return { ok: false, message: `Maximum ${fmt(reste)} (reste à payer)` };
    }
    const newLignes = [...lignes, { id: newLigneId(), methode, montant: m }];
    const nouveauReste = roundMoney(reste - m);
    setLignes(newLignes);
    setMontantSaisi(nouveauReste > 0 ? String(nouveauReste) : '');
    if (nouveauReste > 0.001) {
      setMethode(methode === 'ESPECES' ? 'CARTE' : methode);
    }
    return { ok: true, lignes: newLignes, nouveauReste };
  };

  const handleValider = () => {
    setLocalHint('');

    if (!soldeOk) {
      const added = enregistrerPaiementCourant();
      if (!added.ok) {
        setLocalHint(added.message);
        return;
      }
      if (added.nouveauReste > 0.001) {
        return;
      }
      const finalCheck = validerLignesPaiement(du, added.lignes, montantRecuEspeces);
      if (!finalCheck.ok) {
        setLocalHint(finalCheck.message);
        return;
      }
      if (!processing) onConfirm?.(finalCheck);
      return;
    }

    if (check.ok && !processing) {
      onConfirm?.(check);
    } else {
      setLocalHint(check.message || 'Complétez le paiement');
    }
  };

  const montantCourantValide =
    montantSaisi !== '' &&
    roundMoney(montantSaisi) > 0 &&
    roundMoney(montantSaisi) <= reste + 0.001;

  const peutValider = soldeOk
    ? check.ok && !processing
    : montantCourantValide && !processing;

  const libelleValider = processing
    ? '…'
    : soldeOk
      ? 'Terminer la vente'
      : `Valider · ${fmt(roundMoney(montantSaisi) || 0)} ${labelMethode(methode).toLowerCase()}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {showCodePromo && setCodePromo && (
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5 mb-1.5">
                <Tag className="h-4 w-4" /> Code promo
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={codePromo}
                  onChange={(e) => setCodePromo(e.target.value.toUpperCase())}
                  placeholder="PROMO2025"
                  className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2.5 uppercase focus:border-emerald-500 outline-none"
                />
                {onVerifierPromo && (
                  <button
                    type="button"
                    onClick={onVerifierPromo}
                    disabled={promoLoading || !codePromo.trim()}
                    className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
                  >
                    {promoLoading ? '…' : 'OK'}
                  </button>
                )}
              </div>
              {promoMessage && (
                <p
                  className={`text-xs mt-1.5 ${
                    promoMessage.includes('Réduction') ? 'text-emerald-700 font-medium' : 'text-red-600'
                  }`}
                >
                  {promoMessage}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between items-baseline text-sm text-slate-500 mb-1">
            <span>Total à payer</span>
            {reduction > 0 && montant != null && (
              <span className="line-through text-slate-400">{fmt(montant)}</span>
            )}
          </div>
          <p className="text-3xl font-bold text-slate-900">{du != null ? fmt(du) : '—'}</p>

          {lignes.length > 0 && (
            <ul className="mt-4 space-y-2">
              {lignes.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100"
                >
                  <span className="flex-1 text-sm font-medium text-slate-800">
                    {labelMethode(l.methode)}
                  </span>
                  <span className="font-bold text-slate-900">{fmt(l.montant)}</span>
                  <button
                    type="button"
                    onClick={() => supprimerLigne(l.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                    title="Retirer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {soldeOk && (
            <p className="mt-3 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 border border-emerald-100">
              ✓ Montant entièrement couvert
            </p>
          )}

          {!soldeOk && du != null && (
            <>
              <p className="text-sm text-slate-600 mt-4 mb-2">
                Choisissez le mode et le montant, puis <strong>Valider</strong> — répétez jusqu&apos;à 0 €.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setMethode(id);
                      setMontantSaisi(String(reste));
                    }}
                    className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition ${
                      methode === id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={reste}
                  value={montantSaisi}
                  onChange={(e) => setMontantSaisi(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && peutValider && handleValider()}
                  className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-3 text-lg focus:border-emerald-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setMontantSaisi(String(reste))}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 shrink-0"
                >
                  Tout
                </button>
              </div>
            </>
          )}

          {soldeOk && especesTotal > 0 && (
            <div className="mt-4 space-y-2 p-4 rounded-xl bg-emerald-50/80 border border-emerald-100">
              <p className="text-sm font-medium text-slate-700">
                Espèces reçues du client
                <span className="text-slate-500 font-normal">
                  {' '}
                  (part espèces {fmt(especesTotal)} — modifiez si le client a donné plus pour avoir du rendu)
                </span>
              </p>
              <input
                type="number"
                min="0"
                step="0.01"
                value={montantRecuEspeces}
                onChange={(e) => setMontantRecuEspeces(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && peutValider && handleValider()}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-lg bg-white focus:border-emerald-500 outline-none"
              />
              {rendu != null && (
                <p className="text-sm font-semibold text-emerald-700">Rendu : {fmt(rendu)}</p>
              )}
            </div>
          )}

          {localHint && (
            <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {localHint}
            </p>
          )}
        </div>

        {error && (
          <p className="mx-6 mb-0 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2 shrink-0">
            {error}
          </p>
        )}

        <div className="px-6 py-3 bg-slate-100 border-t border-slate-200 shrink-0">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">Reste à payer</span>
            <span
              className={`text-2xl font-bold ${
                soldeOk ? 'text-emerald-600' : 'text-amber-700'
              }`}
            >
              {du != null ? fmt(Math.max(0, reste)) : '—'}
            </span>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 rounded-xl border border-slate-300 font-medium text-slate-700 hover:bg-white"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleValider}
            disabled={!peutValider}
            className="flex-1 py-3.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-emerald-600/25 text-sm sm:text-base"
          >
            {libelleValider}
          </button>
        </div>
      </div>
    </div>
  );
}
